<template>
  <SfDropdown
    data-testid="visibility-dropdown"
    :drawer-size="'large'"
    :drawer-margin="true"
    :trigger-size="'small'"
    :dropdown-id="visibilityDropdownId"
    :align-option="'left'"
    :is-disabled="isDisabled"
    :is-full-width="true"
    :is-open="isScopedDropdownOpen"
    @dropdown-clicked="handleToggleDropdown"
    @dropdown-closed="closeDropdown"
  >
    <template #trigger>
      {{ visibilityDropdownLabel }}
    </template>

    <template #menu>
      <SfDropdownItem
        :has-border="false"
        :is-selected="isCurrentPeriodChosenForVisibility"
        :is-wide="true"
        :is-wrapped="false"
        @click="selectVisibility(VisibilityLevels.currentPeriod)"
      >
        <div class="sf-level sf-level-between sf-full-width">
          <div class="sf-level-item">
            <div class="sf_level">
              <div class="sf-title">
                {{ ledgerLabel }}
              </div>
              <div class="sf-font-xs">
                {{ ledgerDescriptionLabel }}
              </div>
            </div>
          </div>
          <div class="sf-level-item">
            <span :class="checkedLedgerClass" />
          </div>
        </div>
      </SfDropdownItem>
      <SfDropdownItem
        :has-border="false"
        :is-selected="isPinnedChosenForVisibility"
        :is-wide="true"
        :is-wrapped="true"
        @click="selectVisibility(VisibilityLevels.pinned)"
      >
        <div class="sf-level sf-level-between sf-full-width">
          <div class="sf-level-item">
            <div class="sf_level">
              <div class="sf-title">
                {{ pinnedLabel }}
              </div>
              <div class="sf-font-xs">
                {{ pinnedDescriptionLabel }}
              </div>
            </div>
          </div>
          <div class="sf-level-item">
            <span :class="checkedPinnedClass" />
          </div>
        </div>
      </SfDropdownItem>
      <SfDropdownItem
        v-if="showRecurringOption"
        :has-border="false"
        :is-selected="isRecurringChosenForVisibility"
        :is-wide="true"
        :is-wrapped="true"
        @click="selectVisibility(VisibilityLevels.recurring)"
      >
        <div class="sf-level sf-level-between sf-full-width">
          <div class="sf-level-item">
            <div class="sf_level">
              <div class="sf-title">
                {{ recurringTodoLabel }}
              </div>
              <div class="sf-font-xs">
                {{ recurringTodoDescriptionLabel }}
              </div>
            </div>
          </div>
          <div class="sf-level-item">
            <span :class="checkedRecurringTodoClass" />
          </div>
        </div>
      </SfDropdownItem>
    </template>
  </SfDropdown>
</template>

<script>
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import { dropdowns } from '@app/shared/types/dropdown'
import { useDropdown } from '@app/shared/pinia/dropdown'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'

import { mapActions, mapState } from 'pinia'
import { VisibilityLevels } from './constants'

export default {
  components: {
    SfDropdown,
    SfDropdownItem,
  },
  props: {
    recurringTodoTemplateId: {
      type: Number,
      required: false,
      default: null,
    },
    isEditMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    visibility: {
      type: Number,
      required: true,
    },
    ledgerLabel: {
      type: String,
      required: true,
    },
    isTodo: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['change-visibility'],
  setup() {
    return {
      VisibilityLevels,
      visibilityDropdownId: dropdowns.MESSAGE_VISIBILITY,
      pinnedLabel: I18n.t('communication.remark_form.pinned'),
      ledgerDescriptionLabel: I18n.t(
        'communication.remark_form.ledger_description'
      ),
      recurringTodoLabel: I18n.t(
        'communication.remark_form.recurring_todo_label'
      ),
      recurringTodoDescriptionLabel: I18n.t(
        'communication.remark_form.recurring_todo_description_label'
      ),
    }
  },
  computed: {
    ...mapState(useDropdown, ['currentDropdownId']),
    isScopedDropdownOpen() {
      return this.currentDropdownId === dropdowns.MESSAGE_VISIBILITY
    },
    visibilityDropdownLabel() {
      switch (this.visibility) {
        case VisibilityLevels.pinned:
          return this.pinnedLabel
        case VisibilityLevels.recurring:
          return this.recurringTodoLabel
        default:
          return this.ledgerLabel
      }
    },
    pinnedDescriptionLabel() {
      return this.isTodo
        ? I18n.t('communication.remark_form.pinned_description_todo')
        : I18n.t('communication.remark_form.pinned_description')
    },
    checkedLedgerClass() {
      return this.checkedClass(this.isCurrentPeriodChosenForVisibility)
    },
    checkedPinnedClass() {
      return this.checkedClass(this.isPinnedChosenForVisibility)
    },
    checkedRecurringTodoClass() {
      return this.checkedClass(this.isRecurringChosenForVisibility)
    },
    isCurrentPeriodChosenForVisibility() {
      return this.visibility === VisibilityLevels.currentPeriod
    },
    isPinnedChosenForVisibility() {
      return this.visibility === VisibilityLevels.pinned
    },
    isRecurringChosenForVisibility() {
      return this.visibility === VisibilityLevels.recurring
    },
    showRecurringOption() {
      // Editing a recurring todo
      if (this.recurringTodoTemplateId !== null) {
        return true
      }

      // Editing a non-recurring todo
      if (this.isEditMode) {
        return false
      }

      // Creating a new remark, recurring option is supported only for todos
      return this.isTodo
    },
    isDisabled() {
      return this.recurringTodoTemplateId !== null
    },
  },
  methods: {
    ...mapActions(useDropdown, ['toggleDropdown', 'closeDropdown']),
    handleToggleDropdown() {
      this.toggleDropdown(dropdowns.MESSAGE_VISIBILITY)
    },

    selectVisibility(newVisibility) {
      this.$emit('change-visibility', newVisibility)
      this.closeDropdown()
    },

    checkedClass(checked) {
      return {
        fa: true,
        'fa-check': checked,
      }
    },
  },
}
</script>
